import React, { ChangeEvent } from 'react';

// ── Inline preview formatter (mirrors backend formatTemporal.ts) ────

const EXAMPLE_DURATION = {
    years: 1,
    months: 2,
    weeks: 0,
    days: 3,
    hours: 4,
    minutes: 5,
    seconds: 6,
    milliseconds: 0,
};

function pad(value: number, length: number): string {
    return String(value).padStart(length, '0');
}

const PREVIEW_TOKENS: Array<{ token: string; resolve: () => string }> = [
    { token: 'SSS', resolve: () => pad(EXAMPLE_DURATION.milliseconds, 3) },
    { token: 'MM', resolve: () => pad(EXAMPLE_DURATION.months, 2) },
    { token: 'HH', resolve: () => pad(EXAMPLE_DURATION.hours, 2) },
    { token: 'mm', resolve: () => pad(EXAMPLE_DURATION.minutes, 2) },
    { token: 'ss', resolve: () => pad(EXAMPLE_DURATION.seconds, 2) },
    { token: 'Y', resolve: () => String(EXAMPLE_DURATION.years) },
    { token: 'M', resolve: () => String(EXAMPLE_DURATION.months) },
    { token: 'W', resolve: () => String(EXAMPLE_DURATION.weeks) },
    { token: 'D', resolve: () => String(EXAMPLE_DURATION.days) },
    { token: 'H', resolve: () => String(EXAMPLE_DURATION.hours) },
    { token: 'm', resolve: () => String(EXAMPLE_DURATION.minutes) },
    { token: 's', resolve: () => String(EXAMPLE_DURATION.seconds) },
    { token: 'S', resolve: () => String(EXAMPLE_DURATION.milliseconds) },
];

function formatPreview(pattern: string): string {
    let result = '';
    let index = 0;

    while (index < pattern.length) {
        if (pattern[index] === "'") {
            const closingQuote = pattern.indexOf("'", index + 1);
            if (closingQuote === -1) {
                result += pattern.slice(index + 1);
                break;
            }
            result += pattern.slice(index + 1, closingQuote);
            index = closingQuote + 1;
            continue;
        }

        let matched = false;
        for (const { token, resolve } of PREVIEW_TOKENS) {
            if (pattern.startsWith(token, index)) {
                result += resolve();
                index += token.length;
                matched = true;
                break;
            }
        }

        if (!matched) {
            result += pattern[index];
            index++;
        }
    }

    return result;
}

// ── Component ───────────────────────────────────────────────────────

interface DurationFormatEditorProps {
    nodeState: Record<string, any>;
    setNodeState: (key: string, value: any) => void;
}

const DEFAULT_PATTERN = "Y'years' M'months' D'd' H:mm:ss";

export const DurationFormatEditor: React.FC<DurationFormatEditorProps> = ({ nodeState, setNodeState }) => {
    const pattern = (nodeState.formatPattern as string) || DEFAULT_PATTERN;
    const preview = formatPreview(pattern);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        setNodeState('formatPattern', event.target.value);
    };

    return (
        <div className="flex flex-col gap-1.5 px-2 py-1.5">
            <input
                type="text"
                value={pattern}
                onChange={handleChange}
                placeholder={DEFAULT_PATTERN}
                className="w-52 rounded bg-white/80 text-black text-xs px-1.5 py-1 outline-none focus:ring-1 focus:ring-white"
                spellCheck={false}
            />
            <div className="text-[10px] text-muted-foreground truncate" title={preview}>
                {preview}
            </div>
        </div>
    );
};
